/**
 * Dark mode for Chart.js canvases.
 *
 * Charts are painted on <canvas>, so neither the theme nor the Emotion color adapter can
 * reach them. Pages also draw their own canvas content (doughnut center totals, white slice
 * separators, white plot backgrounds) with light-mode colors. This plugin wraps each chart's
 * 2D context and, while dark mode is active, remaps those colors at draw time with the same
 * rules as darkColorAdapter:
 *
 *  - text (fillText/strokeText)        -> mapText (dark text becomes readable)
 *  - near-white strokes (separators)   -> card surface, so slices stay visually separated
 *  - near-white fills (plot backgrounds) -> card surface
 *
 * Colored data series are never touched.
 */

import { Chart } from "chart.js";
import { parseColor, mapText } from "assets/theme-dark/darkColorAdapter";

const SURFACE = "#131c2e";
const TICK_COLOR = "#94a3b8";
const GRID_COLOR = "rgba(148, 163, 184, 0.14)";

let darkActive = false;
const lightDefaults = { color: Chart.defaults.color, borderColor: Chart.defaults.borderColor };

const isNearWhite = (style) => {
  if (typeof style !== "string") return false;
  const parsed = parseColor(style);
  if (!parsed) return false;
  const [r, g, b, a] = parsed;
  return a > 0.5 && Math.min(r, g, b) >= 235 && Math.max(r, g, b) - Math.min(r, g, b) < 16;
};

function withStyle(ctx, prop, nextStyle, draw) {
  const previous = ctx[prop];
  ctx[prop] = nextStyle;
  try {
    return draw();
  } finally {
    ctx[prop] = previous;
  }
}

function patchContext(ctx) {
  if (!ctx || ctx.$tmsDarkPatched) return;
  ctx.$tmsDarkPatched = true;

  const wrapText = (method, prop) => {
    const original = ctx[method];
    ctx[method] = function patched(...args) {
      if (darkActive && typeof this[prop] === "string") {
        const parsed = parseColor(this[prop]);
        const mapped = parsed && mapText(parsed);
        if (mapped) return withStyle(this, prop, mapped, () => original.apply(this, args));
      }
      return original.apply(this, args);
    };
  };

  const wrapPaint = (method, prop) => {
    const original = ctx[method];
    ctx[method] = function patched(...args) {
      if (darkActive && isNearWhite(this[prop])) {
        return withStyle(this, prop, SURFACE, () => original.apply(this, args));
      }
      return original.apply(this, args);
    };
  };

  wrapText("fillText", "fillStyle");
  wrapText("strokeText", "strokeStyle");
  wrapPaint("stroke", "strokeStyle");
  wrapPaint("strokeRect", "strokeStyle");
  wrapPaint("fill", "fillStyle");
  wrapPaint("fillRect", "fillStyle");
}

export const chartDarkModePlugin = {
  id: "tmsDarkMode",
  beforeInit(chart) {
    patchContext(chart.ctx);
  },
};

Chart.register(chartDarkModePlugin);

/** Switches chart defaults and redraws every mounted chart. */
export function setChartDarkMode(enabled) {
  darkActive = enabled;
  Chart.defaults.color = enabled ? TICK_COLOR : lightDefaults.color;
  Chart.defaults.borderColor = enabled ? GRID_COLOR : lightDefaults.borderColor;

  Object.values(Chart.instances).forEach((chart) => chart.update("none"));
}
